/**
 * Unified body scroll lock hook
 * 
 * Prevents layout shift when modals open by:
 * 1. Using CSS class-based approach (modal-open) instead of inline styles
 * 2. Relying on scrollbar-gutter: stable in global CSS to reserve scrollbar space
 * 3. Never adding padding-right that would cause layout shifts
 * 
 * Usage:
 *   const { lockScroll, unlockScroll } = useBodyScrollLock();
 *   useEffect(() => {
 *     if (isOpen) {
 *       lockScroll();
 *       return () => unlockScroll();
 *     }
 *   }, [isOpen, lockScroll, unlockScroll]);
 */
import { useCallback, useEffect, useRef } from 'react';

let lockCount = 0;

export function useBodyScrollLock() {
  const isLockedRef = useRef(false);

  const lockScroll = useCallback(() => {
    if (isLockedRef.current) {
      return; // Already locked
    }

    lockCount++;
    isLockedRef.current = true;
    document.body.classList.add('modal-open');
  }, []);

  const unlockScroll = useCallback(() => {
    if (!isLockedRef.current) {
      return; // Not locked
    }

    lockCount--;
    isLockedRef.current = false;

    // Only remove class when all locks are released
    if (lockCount <= 0) {
      lockCount = 0; // Safety: prevent negative count
      document.body.classList.remove('modal-open');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isLockedRef.current) {
        lockCount = Math.max(0, lockCount - 1);
        isLockedRef.current = false;
        if (lockCount === 0) {
          document.body.classList.remove('modal-open');
        }
      }
    };
  }, []);

  return { lockScroll, unlockScroll };
}

/**
 * Simple hook that automatically locks/unlocks based on a boolean
 * 
 * Usage:
 *   useBodyScrollLockAuto(isModalOpen);
 */
export function useBodyScrollLockAuto(enabled: boolean) {
  const { lockScroll, unlockScroll } = useBodyScrollLock();

  useEffect(() => {
    if (enabled) {
      lockScroll();
      return () => unlockScroll();
    }
  }, [enabled, lockScroll, unlockScroll]);
}

